import React, { useState, useEffect } from "react";
import { IoMdClose } from "react-icons/io";
import { MdUpgrade } from "react-icons/md";
import Select from "react-select";
import UserService from "../../services/user";
import { toastError, toastSuccess } from "../../components/ToastNotification";

const PLAN_OPTIONS = [
  { value: "AdminProvide", label: "ORGANIZATIONS" },
  { value: "Professionals", label: "Professionals" },
];

const TRANSLATIONS = {
  EN: {
    modalTitle: "Upgrade / Renew Plan",
    modalSubtitle: "Update user plan and subscription expiration",
    currentStatus: "Current Status",
    currentExpiry: "Current Expiry",
    subscription: "Subscription",
    freeTrial: "Free Trial",
    none: "None",
    labelPlanName: "Plan Name",
    labelEndDate: "End Date / Expiry",
    cancel: "Cancel",
    upgrade: "Upgrade Plan",
    upgrading: "Updating...",
    quickSelect: "Quick presets:",
    toastSuccess: "Plan updated successfully!",
    toastError: "Failed to update plan",
  },
  ES: {
    modalTitle: "Actualizar / Renovar Plan",
    modalSubtitle: "Actualizar plan del usuario y vencimiento de suscripción",
    currentStatus: "Estado Actual",
    currentExpiry: "Vencimiento Actual",
    subscription: "Suscripción",
    freeTrial: "Prueba Gratis",
    none: "Ninguna",
    labelPlanName: "Nombre del Plan",
    labelEndDate: "Fecha de Fin / Vencimiento",
    cancel: "Cancelar",
    upgrade: "Actualizar Plan",
    upgrading: "Actualizando...",
    quickSelect: "Accesos rápidos:",
    toastSuccess: "¡Plan actualizado exitosamente!",
    toastError: "Error al actualizar el plan",
  },
};

const getFutureDate = (months = 12) => {
  const d = new Date();
  d.setMonth(d.getMonth() + months);
  return d.toISOString().split("T")[0];
};

const UpgradePlanModal = ({
  isOpen,
  onClose,
  user,
  onPlanUpdated,
  lang = "EN",
}) => {
  const t = TRANSLATIONS[lang] || TRANSLATIONS.EN;

  const [planName, setPlanName] = useState("AdminProvide");
  const [endDate, setEndDate] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen && user?._id) {
      setPlanName("AdminProvide");
      // Default to 1 year from now or 2026-12-31 if today is prior
      const defaultDate = getFutureDate(12);
      setEndDate(defaultDate);
    } else {
      setIsSubmitting(false);
    }
  }, [isOpen, user?._id]);

  const handlePlanChange = (option) => {
    const nextPlan = option?.value || "";
    setPlanName(nextPlan);
    if (nextPlan === "Professionals") {
      setEndDate("");
    } else if (!endDate) {
      setEndDate(getFutureDate(12));
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!user?._id || !planName) return;

    setIsSubmitting(true);
    try {
      const payload = {
        userId: user._id,
        plan_name: planName.trim(),
      };

      if (endDate) {
        const cleanDate = endDate.includes("T") ? endDate.split("T")[0] : endDate;
        payload.endDate = `${cleanDate}T23:59:59.000Z`;
      }

      await UserService.updatePlan(payload);
      toastSuccess(t.toastSuccess);
      if (onPlanUpdated) {
        onPlanUpdated();
      }
      onClose();
    } catch (error) {
      toastError(error?.response?.data?.message || t.toastError);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen || !user) return null;

  const isTrialActive =
    user?.is_free_triee_use &&
    user?.free_trial_expire_date &&
    new Date(user.free_trial_expire_date) > new Date();

  const currentExpiry = user?.subscription_expiresAt || (isTrialActive ? user?.free_trial_expire_date : null);

  return (
    <div
      className="fixed inset-0 bg-black/60 backdrop-blur-sm flex justify-center items-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-3xl shadow-2xl max-w-md w-full overflow-hidden border border-primary/10 transition-all animate-fadeIn"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-primary/10 bg-gradient-to-r from-primary/5 via-white to-primary/5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-primary/10 flex items-center justify-center text-primary shadow-sm">
              <MdUpgrade className="text-2xl" />
            </div>
            <div>
              <h2 className="font-roboto font-bold text-base text-primary tracking-wide">
                {t.modalTitle}
              </h2>
              <p className="font-roboto text-xs text-gray-500 mt-0.5 truncate max-w-[240px]">
                {user.firstName && user.firstName !== "undefined"
                  ? `${user.firstName} · ${user.email}`
                  : user.email}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 rounded-full flex items-center justify-center text-gray-400 hover:text-primary hover:bg-[#F0EEF8] transition-colors"
          >
            <IoMdClose className="text-xl" />
          </button>
        </div>

        {/* Content Form */}
        <form onSubmit={handleSubmit} className="p-6 flex flex-col gap-4">
          {/* Status summary banner */}
          <div className="bg-[#F0EEF8]/60 border border-primary/15 rounded-2xl p-3.5 flex items-center justify-between">
            <div>
              <span className="text-[11px] font-roboto font-semibold text-gray-500 uppercase tracking-wide block">
                {t.currentStatus}
              </span>
              <div className="flex items-center gap-2 mt-1">
                {user.plan_name || user.is_subscription ? (
                  <span className="px-2 py-0.5 rounded-full text-xs font-bold uppercase bg-green-50 text-green-700 border border-green-200">
                    {user.plan_name?.toLowerCase() === "adminprovide"
                      ? "ORGANIZATIONS"
                      : (user.plan_name || t.subscription)}
                  </span>
                ) : isTrialActive ? (
                  <span className="px-2 py-0.5 rounded-full text-xs font-bold uppercase bg-blue-50 text-blue-700 border border-blue-200">
                    {t.freeTrial}
                  </span>
                ) : (
                  <span className="px-2 py-0.5 rounded-full text-xs font-bold uppercase bg-red-50 text-red-500 border border-red-200">
                    {t.none}
                  </span>
                )}
              </div>
            </div>
            <div className="text-right">
              <span className="text-[11px] font-roboto font-semibold text-gray-500 uppercase tracking-wide block">
                {t.currentExpiry}
              </span>
              <span className="text-xs font-roboto font-medium text-gray-700 mt-1 block">
                {currentExpiry ? (
                  new Date(currentExpiry) < new Date() ? (
                    <span className="text-red-500 font-semibold">
                      {new Date(currentExpiry).toLocaleDateString()} (Expired)
                    </span>
                  ) : (
                    new Date(currentExpiry).toLocaleDateString()
                  )
                ) : (
                  "—"
                )}
              </span>
            </div>
          </div>

          {/* Plan Name Field */}
          <div className="flex flex-col gap-1.5">
            <label className="font-roboto text-xs font-semibold text-gray-600 uppercase tracking-wide">
              {t.labelPlanName} <span className="text-red-500">*</span>
            </label>
            <Select
              value={
                PLAN_OPTIONS.find((opt) => opt.value === planName) || {
                  value: planName,
                  label: planName?.toLowerCase() === "adminprovide" ? "ORGANIZATIONS" : planName,
                }
              }
              onChange={handlePlanChange}
              options={PLAN_OPTIONS}
              isSearchable={false}
              menuPlacement="auto"
              menuPosition="fixed"
              menuPortalTarget={document.body}
              className="text-sm font-roboto"
              classNamePrefix="react-select"
              styles={{
                menuPortal: (base) => ({ ...base, zIndex: 99999 }),
              }}
            />
          </div>

          {/* End Date Field */}
          <div className="flex flex-col gap-1.5">
            <label className="font-roboto text-xs font-semibold text-gray-600 uppercase tracking-wide flex items-center justify-between">
              <span>{t.labelEndDate}</span>
              <span className="text-[11px] font-normal text-gray-400 lowercase">
                ({lang === "ES" ? "opcional" : "optional"})
              </span>
            </label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              min={new Date().toISOString().split("T")[0]}
              max="2099-12-31"
              className="w-full rounded-xl px-4 py-2.5 border border-primary/20 focus:outline-none focus:ring-2 focus:ring-primary/20 bg-[#F0EEF8]/40 text-primary font-roboto text-sm cursor-pointer"
            />
            {/* Quick Presets */}
            <div className="flex items-center gap-1.5 flex-wrap mt-1">
              <span className="text-[11px] text-gray-400 mr-1">
                {t.quickSelect}
              </span>
              {[
                { label: "+1M", months: 1 },
                { label: "+3M", months: 3 },
                { label: "+6M", months: 6 },
                { label: "+1Y", months: 12 },
              ].map((preset, idx) => (
                <button
                  key={idx}
                  type="button"
                  onClick={() => setEndDate(getFutureDate(preset.months))}
                  className="px-2 py-0.5 rounded-lg text-[11px] font-roboto font-semibold border border-primary/20 bg-white hover:bg-[#F0EEF8] text-primary transition-colors"
                >
                  {preset.label}
                </button>
              ))}
              {endDate && (
                <button
                  type="button"
                  onClick={() => setEndDate("")}
                  className="px-2 py-0.5 rounded-lg text-[11px] font-roboto font-semibold border border-red-200 bg-red-50 hover:bg-red-100 text-red-500 transition-colors ml-auto"
                >
                  {lang === "ES" ? "Limpiar" : "Clear"}
                </button>
              )}
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex items-center justify-end gap-3 pt-3 border-t border-primary/10 mt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="px-5 py-2.5 rounded-xl font-roboto font-semibold text-xs uppercase tracking-wide text-gray-500 border border-primary/20 hover:bg-[#F0EEF8] transition-all"
            >
              {t.cancel}
            </button>
            <button
              type="submit"
              disabled={isSubmitting || !planName}
              className="px-6 py-2.5 rounded-xl font-roboto font-bold text-xs uppercase tracking-wide text-white bg-primary hover:bg-primary/90 transition-all disabled:opacity-50 shadow-sm flex items-center justify-center gap-2 min-w-[130px]"
            >
              {isSubmitting ? (
                <>
                  <span className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  <span>{t.upgrading}</span>
                </>
              ) : (
                <>
                  <MdUpgrade className="text-base" />
                  <span>{t.upgrade}</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UpgradePlanModal;
